import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  BlockStack,
  InlineStack,
  Tag,
  Text,
  Banner,
  Icon,
  Spinner,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useApi } from '../providers/ApiProvider';
import { t } from '../i18n';

export interface CustomerSegment {
  id: string;
  name: string;
  query?: string;
}

interface SegmentsResponse {
  source: 'shopify' | 'demo_fallback';
  items: CustomerSegment[];
  message?: string;
}

interface CustomerSegmentFieldProps {
  /** Selected segment names (and/or ids stored as "id|name") */
  value: string;
  onChange: (value: string) => void;
  helpText?: string;
}

/**
 * Type-ahead customer segment picker (Shopify Admin segments).
 * Fetches as the merchant types a segment name.
 */
export function CustomerSegmentField({
  value,
  onChange,
  helpText = 'Search Shopify customer segments by name. Select one or more.',
}: CustomerSegmentFieldProps) {
  const api = useApi();
  const [inputValue, setInputValue] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [options, setOptions] = useState<CustomerSegment[]>([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const selected = useMemo(() => parseSegmentValue(value), [value]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(inputValue.trim()), 280);
    return () => window.clearTimeout(t);
  }, [inputValue]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set('q', debouncedQ);
      params.set('first', '20');
      const res = await api.get<SegmentsResponse>(
        `/stores/catalog/segments?${params.toString()}`,
      );
      setOptions(res.items || []);
      setNote(
        res.source === 'shopify'
          ? null
          : res.message || 'Showing sample segments',
      );
    } catch {
      setOptions([]);
      setNote('Could not load segments');
    } finally {
      setLoading(false);
    }
  }, [api, debouncedQ]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedIds = new Set(selected.map((s) => s.id || s.name));

  const autocompleteOptions = options
    .filter((o) => !selectedIds.has(o.id) && !selectedIds.has(o.name))
    .map((o) => ({
      value: o.id,
      label: o.name,
    }));

  function addSegment(id: string) {
    const found = options.find((o) => o.id === id);
    if (!found) return;
    if (selected.some((s) => s.id === found.id || s.name === found.name)) {
      setInputValue('');
      return;
    }
    const next = [...selected, { id: found.id, name: found.name }];
    onChange(serializeSegments(next));
    setInputValue('');
  }

  function removeSegment(nameOrId: string) {
    const next = selected.filter(
      (s) => s.id !== nameOrId && s.name !== nameOrId,
    );
    onChange(serializeSegments(next));
  }

  const textField = (
    <Autocomplete.TextField
      onChange={setInputValue}
      label={t("Customer segments")}
      value={inputValue}
      prefix={<Icon source={SearchIcon} tone="base" />}
      placeholder={t("Start typing a segment name…")}
      autoComplete="off"
      helpText={helpText}
      suffix={loading ? <Spinner size="small" /> : undefined}
    />
  );

  return (
    <BlockStack gap="200">
      {note && (
        <Banner tone="info">
          <p>{note}</p>
        </Banner>
      )}
      <Autocomplete
        options={autocompleteOptions}
        selected={[]}
        onSelect={(vals) => {
          if (vals[0]) addSegment(vals[0]);
        }}
        textField={textField}
        emptyState={
          <Text as="p" tone="subdued">
            {loading
              ? 'Searching…'
              : debouncedQ
                ? `No segments matching “${debouncedQ}”`
                : 'Type to search your Shopify customer segments'}
          </Text>
        }
        loading={loading}
      />
      {selected.length > 0 && (
        <InlineStack gap="100" wrap>
          {selected.map((s) => (
            <Tag
              key={s.id || s.name}
              onRemove={() => removeSegment(s.id || s.name)}
            >
              {s.name}
            </Tag>
          ))}
        </InlineStack>
      )}
    </BlockStack>
  );
}

/** Parse "Name, Name2" or "gid|Name" stored values */
export function parseSegmentValue(value: string): CustomerSegment[] {
  if (!value?.trim()) return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      if (part.includes('|')) {
        const [id, ...rest] = part.split('|');
        return { id: id.trim(), name: rest.join('|').trim() || id.trim() };
      }
      return { id: part, name: part };
    });
}

export function serializeSegments(segments: CustomerSegment[]): string {
  return segments
    .map((s) => (s.id && s.id !== s.name ? `${s.id}|${s.name}` : s.name))
    .join(', ');
}

/** Names only for display / simple config */
export function segmentNamesFromValue(value: string): string[] {
  return parseSegmentValue(value).map((s) => s.name);
}

/** IDs when available for storefront targeting */
export function segmentIdsFromValue(value: string): string[] {
  return parseSegmentValue(value)
    .map((s) => s.id)
    .filter((id) => id.startsWith('gid://') || id.length > 0);
}

/**
 * Rebuild the segment field value from saved campaign advanced.customerEligibility
 * so edit mode restores both names and Shopify GIDs (id|name).
 */
export function segmentValueFromEligibility(elig: {
  segments?: unknown;
  segmentIds?: unknown;
}): string {
  const names = Array.isArray(elig.segments)
    ? (elig.segments as unknown[]).map((s) => String(s || '').trim()).filter(Boolean)
    : [];
  const ids = Array.isArray(elig.segmentIds)
    ? (elig.segmentIds as unknown[]).map((s) => String(s || '').trim()).filter(Boolean)
    : [];
  if (!names.length && !ids.length) return '';
  const n = Math.max(names.length, ids.length);
  const parts: string[] = [];
  for (let i = 0; i < n; i++) {
    const id = ids[i];
    const name = names[i] || id || '';
    if (id && name && id !== name) parts.push(`${id}|${name}`);
    else if (name) parts.push(name);
    else if (id) parts.push(id);
  }
  return parts.join(', ');
}
